// frontend/src/components/engineer/pages/EditOvertimePage.tsx - COMPLETE ANT DESIGN REWRITE + NIGHT SHIFT FIX
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Switch,
  Tag,
  Space,
  Typography,
  Alert,
  Spin,
  Divider,
  Badge,
  Timeline,
  Tooltip,
  Modal,
  Upload,
  Empty,
  Row,
  Col,
  Statistic,
  List,
  Avatar,
  Result,
  message,
  Collapse,
  Form,
  Descriptions,
  Steps
} from 'antd';
import {
  EditOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  UserAddOutlined,
  UploadOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  HistoryOutlined,
  WarningOutlined,
  SaveOutlined,
  ProjectOutlined,
  SwapOutlined,
  MoonFilled
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { AuthUser } from '../../../services/auth.service';
import {
  editOvertimeService,
  EditableOvertimeRequest,
  EditOvertimeData,
  BulkAddResult
} from '../../../services/editOvertime.service';
import { createOvertimeService } from '../../../services/createOvertime.service';
import type { Employee, Approver } from '../../../services/createOvertime.service';
import { duplicateOvertimeCheckService, DuplicateCheckResult } from '../../../services/duplicateOvertimeCheck.service';
import { projectService, Project } from '../../../services/project.service';
import { excelProcessingService, EmployeeMatchResult } from '../../../services/excelProcessing.service';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

interface EditOvertimePageProps {
  requestId: string;
  user?: AuthUser;
  onNavigate?: (page: string) => void;
  onEditComplete?: () => void;
}

// Night shift detection helper
const isNextDayEndTime = (startTime: string, endTime: string): boolean => {
  if (!startTime || !endTime) return false;
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  const startTotal = startHour * 60 + startMinute;
  const endTotal = endHour * 60 + endMinute;
  return endTotal <= startTotal;
};

// Duration calculation with cross-midnight support
const calculateDuration = (startTime: string, endTime: string, isNightShift: boolean): number => {
  if (!startTime || !endTime) return 0;
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  let startTotal = startHour * 60 + startMinute;
  let endTotal = endHour * 60 + endMinute;
  if (isNightShift || endTotal <= startTotal) {
    endTotal += 24 * 60;
  }
  return Math.max(0, (endTotal - startTotal) / 60);
};

const EditOvertimePage: React.FC<EditOvertimePageProps> = ({
  requestId,
  user,
  onNavigate,
  onEditComplete
}) => {
  const [originalRequest, setOriginalRequest] = useState<EditableOvertimeRequest | null>(null);
  const [formData, setFormData] = useState({
    projectName: '',
    projectCode: '',
    startTime: '',
    endTime: '',
    overtimeDate: '',
    isNightShift: false,
    selectedEmployeeIds: [] as string[],
    selectedApprover: null as Approver | null,
    editReason: ''
  });

  const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);
  const [availableApprovers, setAvailableApprovers] = useState<Approver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Project state
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [showProjectSelector, setShowProjectSelector] = useState(false);

  // Bulk upload state
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState<BulkAddResult | null>(null);
  const [excelUploadResult, setExcelUploadResult] = useState<EmployeeMatchResult | null>(null);

  // Employee search state
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);

  // Duplicate checking state
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<DuplicateCheckResult | null>(null);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  const userId = user?.employeeNo || user?.employeeNumber || user?.uid || '';
  const userName = user?.name || 'Unknown User';

  // Auto-detect night shift when times change
  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      const shouldBeNightShift = isNextDayEndTime(formData.startTime, formData.endTime);
      if (shouldBeNightShift && !formData.isNightShift) {
        setFormData(prev => ({ ...prev, isNightShift: true }));
      }
    }
  }, [formData.startTime, formData.endTime]);

  useEffect(() => {
    loadEditData();
  }, [requestId]);

  useEffect(() => {
    checkForChanges();
  }, [formData, originalRequest]);

  // Clear duplicate check when form changes
  useEffect(() => {
    if (duplicateCheckResult) {
      setDuplicateCheckResult(null);
    }
  }, [formData.projectCode, formData.startTime, formData.endTime, formData.overtimeDate, formData.selectedEmployeeIds.length]);

  // Filter employees based on search
  useEffect(() => {
    if (employeeSearchQuery.trim()) {
      const search = employeeSearchQuery.toLowerCase();
      const filtered = availableEmployees.filter(emp => {
        if (formData.selectedEmployeeIds.includes(emp.id)) return false;
        return (
          emp.name.toLowerCase().includes(search) ||
          emp.designation?.toLowerCase().includes(search) ||
          emp.department?.toLowerCase().includes(search) ||
          emp.employeeNumber?.toLowerCase().includes(search) ||
          emp.pin?.toLowerCase().includes(search) ||
          emp.id.toLowerCase().includes(search)
        );
      });
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(
        availableEmployees.filter(emp => !formData.selectedEmployeeIds.includes(emp.id))
      );
    }
  }, [employeeSearchQuery, availableEmployees, formData.selectedEmployeeIds]);

  const loadEditData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const request = await editOvertimeService.getEditableRequest(requestId, userId);
      if (!request) {
        setError('Overtime request not found');
        return;
      }

      if (!request.canEdit) {
        setError('You cannot edit this request. It may have been approved, rejected, or the edit deadline has passed.');
        return;
      }

      setOriginalRequest(request);

      const employees = await createOvertimeService.getEligibleEmployeesForDate(request.overtimeDate);
      setAvailableEmployees(employees);

      const approvers = await createOvertimeService.getAvailableApprovers();
      setAvailableApprovers(approvers);

      const projects = await projectService.getActiveProjects();
      setAvailableProjects(projects);

      const currentApprover = approvers.find(a => a.id === request.approverEmpId);

      // Detect if original request was night shift
      const originalIsNightShift = request.isNightShift || false;

      setFormData({
        projectName: request.projectName,
        projectCode: request.projectCode,
        startTime: request.startTime.toTimeString().slice(0, 5),
        endTime: request.endTime.toTimeString().slice(0, 5),
        overtimeDate: request.overtimeDate.toISOString().split('T')[0],
        isNightShift: originalIsNightShift,
        selectedEmployeeIds: [...request.employeeIds],
        selectedApprover: currentApprover || null,
        editReason: ''
      });

    } catch (err) {
      console.error('Error loading edit data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load request');
    } finally {
      setIsLoading(false);
    }
  };

  const checkForChanges = () => {
    if (!originalRequest) return;

    const hasProjectNameChange = formData.projectName !== originalRequest.projectName;
    const hasProjectCodeChange = formData.projectCode !== originalRequest.projectCode;
    const hasEmployeeChange = JSON.stringify(formData.selectedEmployeeIds.sort()) !==
      JSON.stringify(originalRequest.employeeIds.sort());
    const hasApproverChange = formData.selectedApprover?.id !== originalRequest.approverEmpId;
    const originalDate = originalRequest.overtimeDate.toISOString().split('T')[0];
    const hasDateChange = formData.overtimeDate !== originalDate;
    const originalStartTime = originalRequest.startTime.toTimeString().slice(0, 5);
    const originalEndTime = originalRequest.endTime.toTimeString().slice(0, 5);
    const hasTimeChange = formData.startTime !== originalStartTime ||
      formData.endTime !== originalEndTime;
    const hasNightShiftChange = formData.isNightShift !== (originalRequest.isNightShift || false);

    setHasChanges(
      hasProjectNameChange ||
      hasProjectCodeChange ||
      hasEmployeeChange ||
      hasApproverChange ||
      hasDateChange ||
      hasTimeChange ||
      hasNightShiftChange
    );
  };

  // Duration calculation with night shift support
  const durationHours = useMemo(() => {
    return calculateDuration(formData.startTime, formData.endTime, formData.isNightShift);
  }, [formData.startTime, formData.endTime, formData.isNightShift]);

  // Duplicate check
  const checkForDuplicatesOnEdit = useCallback(async (): Promise<boolean> => {
    if (!originalRequest) return true;

    try {
      setIsCheckingDuplicates(true);
      setDuplicateCheckResult(null);

      const overtimeDate = new Date(formData.overtimeDate);
      const startDateTime = new Date(overtimeDate);
      const endDateTime = new Date(overtimeDate);

      const [startHour, startMinute] = formData.startTime.split(':').map(Number);
      const [endHour, endMinute] = formData.endTime.split(':').map(Number);

      startDateTime.setHours(startHour, startMinute, 0, 0);

      // Handle night shift - end time is next day
      if (formData.isNightShift) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }
      endDateTime.setHours(endHour, endMinute, 0, 0);

      const result = await duplicateOvertimeCheckService.checkDuplicateOvertime(
        formData.selectedEmployeeIds,
        overtimeDate,
        formData.projectCode,
        formData.projectName,
        startDateTime,
        endDateTime,
        requestId
      );

      setDuplicateCheckResult(result);

      if (!result.canProceedWithAll) {
        const blockingCount = result.conflicts.filter(c => !c.canProceed).length;
        message.error(`Cannot save: ${blockingCount} employee(s) have conflicts`);
        return false;
      }

      if (!result.hasConflicts) {
        message.success('No conflicts found');
      }

      return true;

    } catch (err) {
      console.error('Error checking duplicates:', err);
      return true;
    } finally {
      setIsCheckingDuplicates(false);
    }
  }, [formData, originalRequest, requestId]);

  // Save handler with night shift support
  const handleSave = async () => {
    try {
      if (!hasChanges) {
        message.warning('No changes detected');
        return;
      }

      if (!formData.editReason.trim()) {
        message.error('Please provide a reason for editing this request');
        return;
      }

      const canProceed = await checkForDuplicatesOnEdit();
      if (!canProceed) return;

      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const overtimeDate = new Date(formData.overtimeDate);
      const startDateTime = new Date(overtimeDate);
      const endDateTime = new Date(overtimeDate);

      const [startHour, startMinute] = formData.startTime.split(':').map(Number);
      const [endHour, endMinute] = formData.endTime.split(':').map(Number);

      startDateTime.setHours(startHour, startMinute, 0, 0);

      // FIXED: Handle night shift - end time is next day
      if (formData.isNightShift) {
        endDateTime.setDate(endDateTime.getDate() + 1);
      }
      endDateTime.setHours(endHour, endMinute, 0, 0);

      const editData: EditOvertimeData = {
        requestId,
        projectName: formData.projectName,
        projectCode: formData.projectCode,
        startTime: startDateTime,
        endTime: endDateTime,
        overtimeDate: overtimeDate,
        isNightShift: formData.isNightShift,
        employeeIds: formData.selectedEmployeeIds,
        selectedApproverId: formData.selectedApprover?.id,
        selectedApproverName: formData.selectedApprover?.name,
        editReason: formData.editReason,
        editorId: userId,
        editorName: userName
      };

      const result = await editOvertimeService.editOvertimeRequest(editData);

      if (result.success) {
        message.success(result.message);
        setSuccess(result.message);
        setDuplicateCheckResult(null);
        setTimeout(() => {
          if (onEditComplete) {
            onEditComplete();
          } else {
            onNavigate?.('reports-history');
          }
        }, 2000);
      } else {
        message.error(result.message);
        setError(result.message);
      }

    } catch (err) {
      console.error('Error saving changes:', err);
      const errMsg = err instanceof Error ? err.message : 'Failed to save changes';
      message.error(errMsg);
      setError(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  // Project select handler
  const handleProjectSelect = (project: Project) => {
    setFormData(prev => ({
      ...prev,
      projectCode: project.projectCode,
      projectName: project.projectName
    }));
    setShowProjectSelector(false);
    setProjectSearchQuery('');
  };

  // Filtered projects
  const filteredProjects = useMemo(() => {
    if (!projectSearchQuery.trim()) return availableProjects;
    const search = projectSearchQuery.toLowerCase();
    return availableProjects.filter(project =>
      project.projectCode.toLowerCase().includes(search) ||
      project.projectName.toLowerCase().includes(search)
    );
  }, [projectSearchQuery, availableProjects]);

  // Excel upload handler
  const handleExcelUpload = useCallback(async (file: any) => {
    const validation = excelProcessingService.validateExcelFile(file);
    if (!validation.valid) {
      message.error(validation.error || 'Invalid file');
      return false;
    }

    setIsUploadingExcel(true);
    setExcelUploadResult(null);
    setBulkUploadResult(null);

    try {
      const processingResult = await excelProcessingService.processExcelFile(file);
      if (!processingResult.success) {
        throw new Error(processingResult.errors.join(', '));
      }

      const matchingResult = excelProcessingService.matchEmployees(
        processingResult.auxNumbers,
        availableEmployees
      );
      setExcelUploadResult(matchingResult);

      if (matchingResult.matched && matchingResult.matched.length > 0) {
        const matchedIds = matchingResult.matched.map(emp => emp.id);
        const addResult = await editOvertimeService.addEmployeesToRequest(
          requestId, matchedIds, userId, userName
        );
        setBulkUploadResult(addResult);
        if (addResult.success) {
          message.success(addResult.message);
          await loadEditData();
        }
      }
    } catch (err) {
      console.error('Error processing bulk upload:', err);
      message.error(err instanceof Error ? err.message : 'Failed to process upload');
    } finally {
      setIsUploadingExcel(false);
    }

    return false; // Prevent default upload
  }, [availableEmployees, requestId, userId, userName]);

  // Add employee from search
  const handleAddEmployee = (employee: Employee) => {
    if (!formData.selectedEmployeeIds.includes(employee.id)) {
      setFormData(prev => ({
        ...prev,
        selectedEmployeeIds: [...prev.selectedEmployeeIds, employee.id]
      }));
      setEmployeeSearchQuery('');
      message.success(`Added ${employee.name}`);
    }
  };

  // Remove employee
  const handleRemoveEmployee = (employeeId: string) => {
    const emp = availableEmployees.find(e => e.id === employeeId);
    setFormData(prev => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.filter(id => id !== employeeId)
    }));
    if (emp) {
      message.info(`Removed ${emp.name}`);
    }
  };

  // Get employee by ID
  const getEmployeeById = (empId: string): Employee | undefined => {
    return availableEmployees.find(e => e.id === empId);
  };

  // Get status tag color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        background: '#f5f5f5'
      }}>
        <Spin size="large" tip="Loading overtime request..." />
      </div>
    );
  }

  // Error state (no request loaded)
  if (error && !originalRequest) {
    return (
      <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
        <Result
          status="error"
          title="Unable to Load Request"
          subTitle={error}
          extra={
            <Button type="primary" onClick={() => onNavigate?.('reports-history')}>
              Back to Reports
            </Button>
          }
        />
      </div>
    );
  }

  // Get next day label for night shift
  const getNextDayLabel = () => {
    if (!formData.overtimeDate) return '';
    const nextDay = new Date(formData.overtimeDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f0f2f5',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Space align="center" style={{ marginBottom: '16px' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => onNavigate?.('reports-history')}
            >
              Back
            </Button>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Edit Overtime Request
              </Title>
              <Text type="secondary">
                Request ID: {requestId.slice(0, 8)}... | Status: <Tag color={getStatusColor(originalRequest?.status || '')}>{originalRequest?.status?.toUpperCase()}</Tag>
              </Text>
            </div>
          </Space>

          {/* Re-approval warning */}
          {(originalRequest?.status === 'approved' || originalRequest?.status === 'rejected') && (
            <Alert
              message="Re-approval Required"
              description="This request has already been processed. Any changes will reset the status to Pending and require re-approval."
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
        </div>

        {/* Success / Error Alerts */}
        {success && (
          <Alert
            message="Changes Saved Successfully"
            description={success}
            type="success"
            showIcon
            closable
            onClose={() => setSuccess(null)}
            style={{ marginBottom: '16px' }}
          />
        )}

        {error && originalRequest && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Duplicate Check Results */}
        {duplicateCheckResult && duplicateCheckResult.hasConflicts && (
          <Alert
            message={duplicateCheckResult.message}
            description={
              <div>
                <Text>{duplicateCheckResult.conflicts.length} employee(s) with conflicts:</Text>
                <List
                  size="small"
                  dataSource={duplicateCheckResult.conflicts}
                  style={{ marginTop: '8px' }}
                  renderItem={(conflict, index) => (
                    <List.Item key={index}>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: conflict.canProceed ? '#52c41a' : '#ff4d4f',
                              fontSize: '12px'
                            }}
                            size="small"
                          >
                            {conflict.canProceed ? 'OK' : '!'}
                          </Avatar>
                        }
                        title={`${conflict.employeeName} (${conflict.employeePin})`}
                        description={
                          <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Existing: {conflict.existingProjectCode} | {conflict.existingStartTime.toTimeString().slice(0, 5)} - {conflict.existingEndTime.toTimeString().slice(0, 5)}
                            </Text>
                            <br />
                            <Text
                              style={{ fontSize: '12px' }}
                              type={conflict.canProceed ? 'success' : 'danger'}
                            >
                              {conflict.reason}
                            </Text>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />

                {duplicateCheckResult.suggestions.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <Text strong>Suggestions:</Text>
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      {duplicateCheckResult.suggestions.map((s, i) => (
                        <li key={i}><Text type="secondary" style={{ fontSize: '13px' }}>{s}</Text></li>
                      ))}
                    </ul>
                  </div>
                )}

                {!duplicateCheckResult.canProceedWithAll && (
                  <Alert
                    message="Cannot save changes until conflicts are resolved"
                    type="error"
                    style={{ marginTop: '12px' }}
                    showIcon
                  />
                )}
              </div>
            }
            type={duplicateCheckResult.canProceedWithAll ? 'warning' : 'error'}
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Edit History */}
        {originalRequest?.editHistory && originalRequest.editHistory.length > 0 && (
          <Collapse style={{ marginBottom: '16px' }}>
            <Panel
              header={
                <Space>
                  <HistoryOutlined />
                  <Text strong>Edit History ({originalRequest.editHistory.length})</Text>
                </Space>
              }
              key="history"
            >
              <Timeline>
                {originalRequest.editHistory.map((entry, index) => (
                  <Timeline.Item key={index} color="gold">
                    <Text strong>{entry.editedByName}</Text>
                    <Text type="secondary"> - {entry.editedAt.toLocaleString()}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Changes: {entry.changes.map(c => c.field).join(', ')}
                    </Text>
                    {entry.reason && (
                      <>
                        <br />
                        <Text italic style={{ fontSize: '12px' }}>
                          Reason: {entry.reason}
                        </Text>
                      </>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </Panel>
          </Collapse>
        )}

        {/* Main Edit Form */}
        <Card
          title={
            <Space>
              <EditOutlined />
              <span>Edit Request Details</span>
            </Space>
          }
          bordered={false}
          style={{ marginBottom: '24px' }}
        >
          {/* Section 1: Project Information */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={5} style={{ marginBottom: '16px' }}>
              <ProjectOutlined style={{ marginRight: '8px' }} />
              Project Information
            </Title>

            {formData.projectName && formData.projectCode && !showProjectSelector ? (
              <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Selected Project</Text>
                    <br />
                    <Text strong style={{ fontSize: '16px' }}>{formData.projectName}</Text>
                    <br />
                    <Text type="secondary">Code: {formData.projectCode}</Text>
                  </Col>
                  <Col>
                    <Button
                      icon={<SwapOutlined />}
                      onClick={() => setShowProjectSelector(true)}
                    >
                      Change Project
                    </Button>
                  </Col>
                </Row>
              </Card>
            ) : (
              <div>
                <Input
                  placeholder="Search by project code or name..."
                  prefix={<SearchOutlined />}
                  value={projectSearchQuery}
                  onChange={(e) => setProjectSearchQuery(e.target.value)}
                  allowClear
                  disabled={isLoadingProjects}
                  style={{ marginBottom: '12px' }}
                />
                <List
                  size="small"
                  bordered
                  style={{ maxHeight: '300px', overflow: 'auto' }}
                  dataSource={filteredProjects}
                  locale={{ emptyText: projectSearchQuery ? `No projects matching "${projectSearchQuery}"` : 'No projects available' }}
                  renderItem={(project) => (
                    <List.Item
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleProjectSelect(project)}
                      actions={[<Button type="link" size="small">Select</Button>]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<ProjectOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                        title={project.projectName}
                        description={`Code: ${project.projectCode}`}
                      />
                    </List.Item>
                  )}
                />
                {showProjectSelector && formData.projectName && (
                  <Button
                    type="link"
                    onClick={() => setShowProjectSelector(false)}
                    style={{ marginTop: '8px' }}
                  >
                    Cancel - Keep current project
                  </Button>
                )}
              </div>
            )}
          </div>

          <Divider />

          {/* Section 2: Date & Time Schedule */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={5} style={{ marginBottom: '16px' }}>
              <ClockCircleOutlined style={{ marginRight: '8px' }} />
              Date & Time Schedule
            </Title>

            {/* Night shift info */}
            {formData.isNightShift && (
              <Alert
                message="Cross-midnight shift detected"
                description={`End time (${formData.endTime}) will be treated as next day (${getNextDayLabel()}). Duration: ${durationHours.toFixed(1)} hours.`}
                type="info"
                icon={<MoonFilled />}
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            <Row gutter={16}>
              <Col span={6}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '13px' }}>Overtime Date</Text>
                </div>
                <Input
                  type="date"
                  value={formData.overtimeDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, overtimeDate: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={5}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '13px' }}>Start Time</Text>
                </div>
                <Input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={5}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '13px' }}>
                    End Time {formData.isNightShift && <Tag color="blue" style={{ marginLeft: '4px', fontSize: '10px' }}>Next Day</Tag>}
                  </Text>
                </div>
                <Input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={4}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '13px' }}>Cross-Midnight</Text>
                </div>
                <Switch
                  checked={formData.isNightShift}
                  onChange={(checked) => setFormData(prev => ({ ...prev, isNightShift: checked }))}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  style={{ marginTop: '4px' }}
                />
              </Col>
              <Col span={4}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong style={{ fontSize: '13px' }}>Duration</Text>
                </div>
                <Statistic
                  value={durationHours.toFixed(1)}
                  suffix="hours"
                  valueStyle={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: durationHours > 0 ? '#1890ff' : '#ff4d4f'
                  }}
                />
              </Col>
            </Row>

            {/* Time summary */}
            {durationHours > 0 && (
              <Card size="small" style={{ marginTop: '16px', background: '#fafafa' }}>
                <Text>
                  Schedule: {formData.startTime} → {formData.endTime}
                  {formData.isNightShift && ` (ends ${getNextDayLabel()})`}
                  {' | '}Total: <Text strong>{durationHours.toFixed(1)} hours</Text>
                </Text>
              </Card>
            )}
          </div>

          <Divider />

          {/* Section 3: Employees */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={5} style={{ marginBottom: '16px' }}>
              <TeamOutlined style={{ marginRight: '8px' }} />
              Employees ({formData.selectedEmployeeIds.length})
            </Title>

            {/* Add Employee Search */}
            <Card
              size="small"
              title={
                <Space>
                  <UserAddOutlined />
                  <span>Add Employee</span>
                </Space>
              }
              style={{ marginBottom: '16px' }}
            >
              <Input
                placeholder="Search by name, AUX number, PIN, or employee ID..."
                prefix={<SearchOutlined />}
                value={employeeSearchQuery}
                onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                allowClear
                style={{ marginBottom: '8px' }}
              />

              {employeeSearchQuery && (
                <List
                  size="small"
                  bordered
                  style={{ maxHeight: '250px', overflow: 'auto' }}
                  dataSource={filteredEmployees.slice(0, 20)}
                  locale={{ emptyText: `No employees found matching "${employeeSearchQuery}"` }}
                  renderItem={(employee) => (
                    <List.Item
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleAddEmployee(employee)}
                      actions={[
                        <Button
                          type="link"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddEmployee(employee);
                          }}
                        >
                          Add
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: '#52c41a' }}>
                            {employee.name.charAt(0)}
                          </Avatar>
                        }
                        title={employee.name}
                        description={`PIN: ${employee.pin || employee.employeeNumber || 'N/A'} | ${employee.designation || ''} ${employee.department ? '| ' + employee.department : ''}`}
                      />
                    </List.Item>
                  )}
                  footer={
                    filteredEmployees.length > 20 ? (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Showing first 20 results. Refine your search for more specific results.
                      </Text>
                    ) : null
                  }
                />
              )}
            </Card>

            {/* Bulk Upload */}
            <Collapse style={{ marginBottom: '16px' }}>
              <Panel
                header={
                  <Space>
                    <FileExcelOutlined />
                    <span>Bulk Upload via Excel</span>
                  </Space>
                }
                key="bulk"
              >
                <Alert
                  message="Upload Excel file with AUX numbers. New employees will be added, duplicates will be skipped."
                  type="info"
                  showIcon
                  style={{ marginBottom: '12px' }}
                />
                <Upload
                  accept=".xlsx,.xls"
                  beforeUpload={handleExcelUpload}
                  showUploadList={false}
                  disabled={isUploadingExcel}
                >
                  <Button icon={<UploadOutlined />} loading={isUploadingExcel}>
                    {isUploadingExcel ? 'Processing...' : 'Upload Excel File'}
                  </Button>
                </Upload>

                {bulkUploadResult && (
                  <Alert
                    message={bulkUploadResult.message}
                    description={
                      <div>
                        {bulkUploadResult.addedCount > 0 && (
                          <Text type="success">Added: {bulkUploadResult.addedCount} employees</Text>
                        )}
                        {bulkUploadResult.skippedCount > 0 && (
                          <>
                            <br />
                            <Text type="warning">Skipped (already exist): {bulkUploadResult.skippedCount}</Text>
                          </>
                        )}
                      </div>
                    }
                    type={bulkUploadResult.success ? 'success' : 'error'}
                    showIcon
                    style={{ marginTop: '12px' }}
                  />
                )}
              </Panel>
            </Collapse>

            {/* Selected Employees List */}
            <Card
              size="small"
              title={`Selected Employees (${formData.selectedEmployeeIds.length})`}
              extra={
                formData.selectedEmployeeIds.length > 0 && (
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => {
                      Modal.confirm({
                        title: 'Clear all employees?',
                        content: 'This will remove all selected employees from this request.',
                        onOk: () => setFormData(prev => ({ ...prev, selectedEmployeeIds: [] }))
                      });
                    }}
                  >
                    Clear All
                  </Button>
                )
              }
              bodyStyle={{ maxHeight: '350px', overflow: 'auto' }}
            >
              {formData.selectedEmployeeIds.length > 0 ? (
                <List
                  size="small"
                  dataSource={formData.selectedEmployeeIds}
                  renderItem={(empId) => {
                    const employee = getEmployeeById(empId);
                    return (
                      <List.Item
                        actions={[
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveEmployee(empId)}
                          />
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar style={{ backgroundColor: '#1890ff' }} size="small">
                              {employee?.name?.charAt(0) || '?'}
                            </Avatar>
                          }
                          title={
                            <Text style={{ fontSize: '13px' }}>
                              {employee?.name || 'Unknown Employee'}
                            </Text>
                          }
                          description={
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              PIN: {employee?.pin || employee?.employeeNumber || empId} | {employee?.designation || 'N/A'}
                            </Text>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No employees selected. Use the search above to add employees."
                />
              )}
            </Card>
          </div>

          <Divider />

          {/* Section 4: Approver */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={5} style={{ marginBottom: '16px' }}>
              <CheckCircleOutlined style={{ marginRight: '8px' }} />
              Approver
            </Title>
            <Select
              style={{ width: '100%' }}
              placeholder="Select Approver"
              value={formData.selectedApprover?.id || undefined}
              onChange={(value) => {
                const approver = availableApprovers.find(a => a.id === value);
                setFormData(prev => ({ ...prev, selectedApprover: approver || null }));
              }}
              showSearch
              optionFilterProp="children"
            >
              {availableApprovers.map(approver => (
                <Option key={approver.id} value={approver.id}>
                  {approver.name} - {approver.designation} ({approver.department})
                </Option>
              ))}
            </Select>
          </div>

          <Divider />

          {/* Section 5: Edit Reason */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={5} style={{ marginBottom: '16px' }}>
              <InfoCircleOutlined style={{ marginRight: '8px' }} />
              Reason for Edit <Text type="danger">*</Text>
            </Title>
            <TextArea
              value={formData.editReason}
              onChange={(e) => setFormData(prev => ({ ...prev, editReason: e.target.value }))}
              placeholder="Please explain why you are editing this request..."
              rows={3}
              showCount
              maxLength={500}
            />
          </div>

          {/* Change Summary */}
          {hasChanges && (
            <Alert
              message="Changes Detected"
              description={
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {formData.projectName !== originalRequest?.projectName && <li>Project name changed</li>}
                  {formData.projectCode !== originalRequest?.projectCode && <li>Project code changed</li>}
                  {JSON.stringify(formData.selectedEmployeeIds.sort()) !== JSON.stringify(originalRequest?.employeeIds.sort()) && (
                    <li>Employee selection changed ({formData.selectedEmployeeIds.length} selected, was {originalRequest?.employeeIds.length})</li>
                  )}
                  {formData.selectedApprover?.id !== originalRequest?.approverEmpId && <li>Approver changed</li>}
                  {formData.overtimeDate !== originalRequest?.overtimeDate.toISOString().split('T')[0] && <li>Date changed</li>}
                  {formData.startTime !== originalRequest?.startTime.toTimeString().slice(0, 5) && <li>Start time changed</li>}
                  {formData.endTime !== originalRequest?.endTime.toTimeString().slice(0, 5) && <li>End time changed</li>}
                  {formData.isNightShift !== (originalRequest?.isNightShift || false) && <li>Night shift setting changed</li>}
                </ul>
              }
              type="warning"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid #f0f0f0'
          }}>
            <Button
              onClick={() => onNavigate?.('reports-history')}
              disabled={isSaving || isCheckingDuplicates}
            >
              Cancel
            </Button>
            <Button
              icon={<SearchOutlined />}
              onClick={checkForDuplicatesOnEdit}
              loading={isCheckingDuplicates}
              disabled={isSaving || formData.selectedEmployeeIds.length === 0}
            >
              Check Conflicts
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={isSaving}
              disabled={
                !hasChanges ||
                isCheckingDuplicates ||
                isUploadingExcel ||
                !formData.editReason.trim() ||
                formData.selectedEmployeeIds.length === 0 ||
                (duplicateCheckResult ? !duplicateCheckResult.canProceedWithAll : false)
              }
            >
              Save Changes
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EditOvertimePage;